import { useState, useEffect, useRef } from "react";
import "./App.css";

function App() {
  const [activeSection, setActiveSection] = useState("about");

  const sectionRefs = {
    about: useRef<HTMLDivElement>(null),
    bots: useRef<HTMLDivElement>(null),
    sites: useRef<HTMLDivElement>(null),
    servers: useRef<HTMLDivElement>(null),
  };

  useEffect(() => {
    const handleScroll = () => {
      let lastVisibleSection = activeSection;
      Object.keys(sectionRefs).forEach((key) => {
        const ref = sectionRefs[key as keyof typeof sectionRefs].current;
        if (ref) {
          const rect = ref.getBoundingClientRect();
          if (
            rect.top <= window.innerHeight / 2 &&
            rect.bottom > window.innerHeight / 4
          ) {
            lastVisibleSection = key;
          }
        }
      });
      setActiveSection(lastVisibleSection);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection, sectionRefs]);

  const linksRef = useRef<HTMLAnchorElement[]>([]);
  useEffect(() => {
    const links = linksRef.current;
    const handleMouseMove = (e: MouseEvent, link: HTMLAnchorElement) => {
      const rect = link.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const deltaX = mouseX - centerX;
      const deltaY = mouseY - centerY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const maxDistance = 100;
      if (distance < maxDistance) {
        const pushStrength = 10;
        const pushX = (deltaX / distance) * pushStrength;
        const pushY = (deltaY / distance) * pushStrength;
        link.style.transform = `translate(calc(-50% + ${pushX}px), calc(-50% + ${pushY}px))`;
      }
    };
    const handleMouseLeave = (link: HTMLAnchorElement) => {
      link.style.transform = "translate(-50%, -50%)";
    };
    links.forEach((link) => {
      link.addEventListener("mousemove", (e) => handleMouseMove(e, link));
      link.addEventListener("mouseleave", () => handleMouseLeave(link));
    });
    return () => {
      links.forEach((link) => {
        link.removeEventListener("mousemove", (e) => handleMouseMove(e, link));
        link.removeEventListener("mouseleave", () => handleMouseLeave(link));
      });
    };
  }, []);
  const addLinkToRefs = (el: HTMLAnchorElement | null) => {
    if (el && !linksRef.current.includes(el)) {
      linksRef.current.push(el);
    }
  };

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-300 bg-[#151515] text-gray-200">
      {/* Навигация */}
      <div className="navbar fixed top-5 left-1/2 -translate-x-1/2 bg-[#232323] shadow-md flex justify-center px-6 py-2 rounded-xl z-50 gap-2">
        <button
          className={`px-4 py-2 rounded-md transition font-medium ${
            activeSection === "about"
              ? "bg-[#555] text-white"
              : "text-white hover:bg-[#444]"
          }`}
          onClick={() => {
            setActiveSection("about");
            document
              .getElementById("about")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Обо мне
        </button>
        <button
          className={`px-4 py-2 rounded-md transition font-medium ${
            activeSection === "bots"
              ? "bg-[#555] text-white"
              : "text-white hover:bg-[#444]"
          }`}
          onClick={() => {
            setActiveSection("bots");
            document
              .getElementById("bots")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Боты
        </button>
        <button
          className={`px-4 py-2 rounded-md transition font-medium ${
            activeSection === "sites"
              ? "bg-[#555] text-white"
              : "text-white hover:bg-[#444]"
          }`}
          onClick={() => {
            setActiveSection("sites");
            document
              .getElementById("sites")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Сайты
        </button>
        <button
          className={`px-4 py-2 rounded-md transition font-medium ${
            activeSection === "servers"
              ? "bg-[#555] text-white"
              : "text-white hover:bg-[#444]"
          }`}
          onClick={() => {
            setActiveSection("servers");
            document
              .getElementById("servers")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Серверы
        </button>
      </div>

      {/* Секция "Обо мне" */}
      <div id="about" ref={sectionRefs.about} className="content pt-24">
        <div className="avatar-container flex justify-center items-center min-h-[80vh] relative">
          <img
            src="/Baldwin.jpg"
            alt="Avatar"
            className="avatar w-[350px] h-[350px] rounded-full object-cover shadow-lg"
          />
          <div className="links-container absolute w-[550px] h-[550px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <a
              href="https://github.com/Fe1kerok"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="GitHub"
            >
              <i className="fab fa-github"></i>
            </a>
            <a
              href="https://vk.com/fe1ker"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="VK"
            >
              <i className="fab fa-vk"></i>
            </a>
            <a
              href="https://t.me/Fe1ker"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="Telegram"
            >
              <i className="fab fa-telegram"></i>
            </a>
            <a
              href="https://www.donationalerts.com/r/fe1ker"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="Donate"
            >
              <i className="fas fa-coins"></i>
            </a>
            <a
              href="https://instagram.com/fe1ker"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
            <a
              href="https://discord.gg/MSs85sfgb9"
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              ref={addLinkToRefs}
              data-label="Discord"
            >
              <i className="fab fa-discord"></i>
            </a>
          </div>
        </div>
        <div className="about-panel mx-auto mt-24 max-w-2xl text-center bg-[#232323] shadow-lg rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-2 text-white">
            Привет, я Fe1ker
          </h2>
          <p className="text-[#cccccc]">
            Балуюсь на Python и JavaScript. Здесь вы можете посмотреть список
            моих ботов и сайтов. Если хотите поддержать или связаться со мной,
            воспользуйтесь ссылками выше.
          </p>
        </div>
      </div>

      {/* Секция "Мои боты" */}
      <div id="bots" ref={sectionRefs.bots} className="content pt-24">
        <h2 className="text-2xl font-bold mb-8 text-white text-center">
          Мои боты
        </h2>
        <div className="flex flex-wrap gap-8 justify-center">
          <a
            href="https://eco-bot.ru/"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/EcoNewBig.jpg"
              alt="Аватарка"
              className="bot-avatar w-40 h-40 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">Eco</h3>
            <p className="text-gray-400 mb-2">Универсальный бот</p>
            <div className="font-bold mt-2 text-white">
              В активной разработке
            </div>
          </a>
          <div className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer">
            <img
              src="/lowmodelogo.png"
              alt="Аватарка"
              className="bot-avatar w-40 h-40 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">LowMode</h3>
            <p className="text-gray-400 mb-2">Бот для модерации на сервере</p>
            <div className="font-bold mt-2 text-white">
              В стабильной заморозке
            </div>
          </div>
        </div>
      </div>

      {/* Секция "Мои сайты" */}
      <div id="sites" ref={sectionRefs.sites} className="content pt-24">
        <h2 className="text-2xl font-bold mb-8 text-white text-center">
          Мои сайты
        </h2>
        <div className="flex flex-wrap gap-8 justify-center">
          <a
            href="https://eco-bot.ru/"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-green-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/EcoNewBig.jpg"
              alt="Eco Bot"
              className="w-32 h-32 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">Eco Bot</h3>
            <p className="text-gray-400 mb-2">eco-bot.ru</p>
            <div className="font-bold mt-2 text-white">
              В активной разработке
            </div>
          </a>
          <a
            href="https://discordtag.ru/"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/discordtag.png"
              alt="DiscordTag"
              className="w-32 h-32 object-cover mx-auto mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">DiscordTag</h3>
            <p className="text-gray-400 mb-2">discordtag.ru</p>
            <div className="font-bold mt-2 text-white">
              В стабильной заморозке
            </div>
          </a>
          <a
            href="https://pulseplay.ru/"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-purple-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/pulseplay.png"
              alt="PulsePlay"
              className="w-32 h-32 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">PulsePlay</h3>
            <p className="text-gray-400 mb-2">pulseplay.ru</p>
            <div className="font-bold mt-2 text-white">
              В стабильной заморозке
            </div>
          </a>
        </div>
      </div>

      {/* Секция "Серверы" */}
      <div
        id="servers"
        ref={sectionRefs.servers}
        className="content pt-24 pb-24"
      >
        <h2 className="text-2xl font-bold mb-8 text-white text-center">
          Серверы
        </h2>
        <div className="flex flex-wrap gap-8 justify-center">
          <a
            href="https://discord.gg/MSs85sfgb9"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/pulseplay.png"
              alt="Аватарка"
              className="server-avatar w-40 h-40 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">PulseHub</h3>
            <p className="text-gray-400 mb-2">
              Сервер для общения и развлечений.
            </p>
          </a>
          <a
            href="https://discord.gg/Ks8QDP84ed"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/rouleteloungelogo.webp"
              alt="Аватарка"
              className="server-avatar w-40 h-40 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">
              🔻Roulette Lounge🔺
            </h3>
            <p className="text-gray-400 mb-2">
              Это сервер для развлечения и общения.
            </p>
          </a>
          <a
            href="https://discord.gg/Bket4CxKve"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card p-8 rounded-xl bg-[#232323] transition-transform transform hover:scale-105 hover:shadow-lg hover:border-blue-400 border border-transparent w-80 text-center cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/TsarskayaArmy.webp"
              alt="Аватарка"
              className="server-avatar w-40 h-40 rounded-full object-cover mx-auto shadow mb-4"
            />
            <h3 className="text-xl font-bold mb-2 text-white">
              Tsarskaya Army
            </h3>
            <p className="text-gray-400 mb-2">
              Клановый сервер для игры в Chivalry 2.
            </p>
          </a>
        </div>
      </div>
    </div>
  );
}

export default App;
